import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, ShoppingBag, Info } from 'lucide-react';
import Button from '@components/common/Button';
import { removeFromCart, updateQuantity } from '@store/slices/cartSlice';
import { ROUTES } from '@constants';

const CartView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, totalAmount, totalItems } = useSelector((state) => state.cart);
  const [showModal, setShowModal] = useState(false);

  const handleRemoveItem = (productId) => {
    dispatch(removeFromCart(productId));
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      dispatch(removeFromCart(productId));
    } else {
      dispatch(updateQuantity({ id: productId, quantity: newQuantity }));
    }
  };

  const handleCheckout = () => {
    setShowModal(true);
  };

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <ShoppingCart className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
        <p className="text-sm sm:text-base text-gray-600 mb-6 text-center max-w-md">
          Looks like you haven't added any items to your cart yet. Start shopping to add items!
        </p>
        <Button variant="primary" size="lg" onClick={() => window.history.back()}>
          Continue Shopping
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <div className="w-full px-3 py-3">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
          <div className="lg:col-span-8">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <ShoppingBag className="w-6 h-6 text-navbar" />
                  Shopping Cart
                </h2>
                <span className="text-sm text-gray-600 font-medium">
                  {totalItems} {totalItems === 1 ? 'item' : 'items'}
                </span>
              </div>

              <div className="divide-y divide-gray-200 max-h-[calc(100vh-180px)] overflow-y-auto">
                {items.map((item) => (
                  <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-bold text-gray-900 mb-1 line-clamp-2">
                              {item.title}
                            </h3>
                            {item.author && (
                              <p className="text-sm text-gray-600">
                                by {item.author}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                            aria-label="Remove item"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>

                        {item.badge && (
                          <span className="inline-block px-2.5 py-0.5 bg-primary-50 text-navbar text-xs font-semibold rounded-full border border-primary-200 mb-2">
                            {item.badge}
                          </span>
                        )}

                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center bg-gray-100 rounded-lg border border-gray-300">
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                className="w-9 h-9 rounded-l-lg hover:bg-gray-200 flex items-center justify-center text-gray-700 transition-colors"
                                aria-label="Decrease quantity"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-12 text-center text-base font-semibold text-gray-900">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                className="w-9 h-9 rounded-r-lg hover:bg-gray-200 flex items-center justify-center text-gray-700 transition-colors"
                                aria-label="Increase quantity"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">
                              ₹{(item.price * item.quantity).toLocaleString()}
                            </div>
                            {item.originalPrice && (
                              <div className="text-sm text-gray-400 line-through">
                                ₹{(item.originalPrice * item.quantity).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm sticky top-20">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Order Summary</h3>
              </div>
              
              <div className="px-4 py-3 space-y-2.5">
                <div className="flex items-center justify-between text-base">
                  <span className="text-gray-600">Subtotal ({totalItems} items)</span>
                  <span className="font-semibold text-gray-900">₹{totalAmount.toLocaleString()}</span>
                </div>
                
                <div className="flex items-center justify-between text-base">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-semibold text-green-600">FREE</span>
                </div>
                
                <div className="flex items-center justify-between text-base">
                  <span className="text-gray-600">Tax</span>
                  <span className="font-semibold text-gray-900">₹0</span>
                </div>
              </div>

              <div className="px-4 py-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-lg font-bold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-navbar">
                    ₹{totalAmount.toLocaleString()}
                  </span>
                </div>

                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={handleCheckout}
                  className="mb-2"
                >
                  Proceed to Checkout
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={() => navigate(ROUTES.CART)}
                >
                  Continue Shopping
                </Button>

                <div className="mt-3 p-2.5 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex gap-2">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-900">
                      <span className="font-semibold">Free shipping</span> on all orders. No minimum purchase required!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
        >
          <div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-100">
                  <Info className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Coming Soon!</h3>
                  <p className="text-sm text-gray-600">
                    The checkout feature will be introduced in the future. We're working hard to bring you a seamless payment experience. Stay tuned!
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
              <Button variant="primary" onClick={() => setShowModal(false)}>
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartView;
